import { Link, useLocation } from 'react-router-dom'
import { FileSpreadsheet, Menu, X, User, LogOut, MessageSquare, CalendarDays, Bell, Sheet, LayoutDashboard, Gift, Shield, CreditCard, Banknote, Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { useDepositActionCount } from '@/hooks/useDepositActionCount'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useLoginModal } from '@/contexts/LoginModalContext'
import { useState } from 'react'
import { ChannelTalk } from '@/lib/channel'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

export default function NewNavigationBar() {
  const location = useLocation()
  const { user, signOut } = useAuth()
  const { openLoginModal } = useLoginModal()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // 입금매칭 배지: linked&&count>=1 → 숫자 pill / 그 외(미연동·판정전·0건·실패) → 없음.
  // 카운트 정의 = /deposits '확인 대기'와 1:1.
  const { accountStatus: depositAccountStatus, count: depositActionCount } = useDepositActionCount()
  const showDepositCount =
    depositAccountStatus === 'linked' && depositActionCount != null && depositActionCount >= 1

  // 어드민 유저 ID
  const ADMIN_USER_IDS = ['bd873ddc-9c11-4093-a4e6-a2fb74e2cb71']
  const isAdmin = user && ADMIN_USER_IDS.includes(user.id)

  const navItems = [
    { path: '/dashboard', label: '대시보드', icon: LayoutDashboard },
    { path: '/order', label: '정산서', icon: FileSpreadsheet },
    { path: '/deposits', label: '입금매칭', icon: Banknote },
    // 재고관리 전 셀러 공개(런칭 2026-07-17 — adminOnly 제거)
    { path: '/inventory', label: '재고관리', icon: Package },
    { path: '/excel', label: '배송엑셀', icon: Sheet },
    { path: '/today-sales', label: '오늘얼마', icon: CalendarDays },
    { path: '/invite', label: '초대장', icon: Gift, isHot: true },
  ]
  // adminOnly 게이트 항목 0(재고관리 런칭으로 마지막 항목 공개) — 필터는 항등이라 제거.
  // isAdmin은 아래 어드민 전용 메뉴에서 계속 사용.
  const visibleNavItems = navItems

  // 채널톡 열기 함수
  const openChannelTalk = () => {
    ChannelTalk.open()
    if (isMenuOpen) {
      setIsMenuOpen(false)
    }
  }

  // 확인 대기 카운트 pill — 블루 액센트 1색, 10건↑ '9+'.
  const renderCountBadge = (n: number) => (
    <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide bg-blue-600 text-white rounded-full">
      {n > 9 ? '9+' : n}
    </span>
  )

  // 입금매칭 항목 전용 배지. null = 배지 없음(미연동·판정전·0건·실패).
  const renderDepositBadge = () => {
    if (!showDepositCount || depositActionCount == null) return null
    return renderCountBadge(depositActionCount)
  }

  // HOT 배지 렌더링 함수 (초대장용) — 프로모션 웜 액센트 1색만 유지 (그라데이션·이모지 제거)
  const renderHotBadge = () => (
    <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide bg-orange-700 text-white rounded-full">
      HOT
    </span>
  )

  return (
    // 흰 밴드: 페이지 배경(slate-50)과 명확히 분리되는 띠 — 보더+그림자로 경계
    <nav className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-5">
        <div className="flex items-center h-14">
          {/* 로고 - 클릭 시 홈으로 이동 */}
          <Link to="/" className="text-2xl font-bold text-brand-dark hover:opacity-80 transition-opacity">
          SellerHelper
          </Link>

          {/* 데스크톱 메뉴 — nowrap 항목이 창보다 넓어지면 겹침/줄바꿈 대신 내부 가로 스크롤 */}
          <div className="hidden md:flex flex-1 justify-center min-w-0">
            <div className="flex gap-1 overflow-x-auto min-w-0">
              {visibleNavItems.map((item) => {
                const isActive = location.pathname === item.path
                const Icon = item.icon
                return (
                  <TooltipProvider key={item.path} delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {/* 활성 = 항목 하단 브랜드색 바 + 진한 텍스트 + 브랜드색 아이콘(호버 pill과
                            채널 분리). 호버 pill은 비활성 항목에만. 바는 링크 박스 안에 두어
                            클래식 스크롤바가 스크롤포트 높이를 깎아도 위치가 밀리지 않음
                            (teal은 2.5:1이라 보조 신호 — 주 신호는 slate-900 텍스트 + aria-current) */}
                        <Link
                          to={item.path}
                          aria-current={isActive ? 'page' : undefined}
                          className={`relative flex items-center shrink-0 whitespace-nowrap px-2 py-2 rounded-md text-sm font-medium transition-colors
                            ${isActive
                              ? 'text-slate-900'
                              : 'text-slate-600 hover:bg-slate-100/70 hover:text-slate-900'
                            }`}
                        >
                          <Icon className={`h-4 w-4 mr-1.5 ${isActive ? 'text-brand-primary' : ''}`} />
                          <span className="flex items-center">
                            {item.label}
                            {item.path === '/deposits' && renderDepositBadge()}
                            {item.isHot && renderHotBadge()}
                          </span>
                          {isActive && (
                            <span aria-hidden="true" className="absolute inset-x-2 bottom-0 h-[2.5px] rounded-full bg-brand-primary" />
                          )}
                        </Link>
                      </TooltipTrigger>
                      {item.path === '/deposits' && showDepositCount && (
                        <TooltipContent side="bottom" className="bg-slate-800 text-white">
                          <p>확인 대기 입금 {depositActionCount}건</p>
                        </TooltipContent>
                      )}
                      {item.isHot && (
                        <TooltipContent side="bottom" className="bg-slate-800 text-white">
                          <p>친구 초대하고 보상 받으세요! 🎁</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )
              })}
              {/* 고객센터 버튼 추가 */}
              <button
                onClick={openChannelTalk}
                className="flex items-center shrink-0 whitespace-nowrap px-2 py-2 rounded-md text-sm font-medium transition-colors text-slate-600 hover:bg-slate-100/70 hover:text-slate-900"
              >
                <MessageSquare className="h-4 w-4 mr-1.5" />
                고객센터
              </button>
            </div>
          </div>

          {/* 프로필/로그인 영역 */}
          <div className="flex items-center gap-2 ml-auto">
            {/* 공지사항 — 네비 항목에서 분리한 종 아이콘 버튼. 모바일은 좁은 헤더 오버플로 방지를
                위해 숨기고 햄버거 메뉴 항목으로 대체 */}
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/notice"
                    aria-label="공지사항"
                    aria-current={location.pathname === '/notice' ? 'page' : undefined}
                    className={`relative hidden md:flex items-center p-2 rounded-md transition-colors
                      ${location.pathname === '/notice'
                        ? 'text-slate-900'
                        : 'text-slate-600 hover:bg-slate-100/70 hover:text-slate-900'
                      }`}
                  >
                    <Bell className="h-5 w-5" />
                    {location.pathname === '/notice' && (
                      <span aria-hidden="true" className="absolute inset-x-1.5 bottom-0 h-[2.5px] rounded-full bg-brand-primary" />
                    )}
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-slate-800 text-white">
                  <p>공지사항</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.user_metadata.avatar_url} />
                      <AvatarFallback>{user.user_metadata.nickname?.[0]}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-sm">
                      {user.user_metadata.nickname} 님
                    </span>
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white border border-slate-200 shadow-md">
                  <DropdownMenuLabel className="bg-slate-50 text-slate-700">
                    {user.user_metadata.nickname} 님
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-200" />
                  <DropdownMenuItem asChild className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">
                    <Link to="/mypage" className="flex items-center w-full hover:bg-slate-100 rounded-md transition-colors">
                      <User className="mr-2 h-4 w-4" />
                      <span>마이 페이지</span>
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuItem asChild className="cursor-pointer focus:bg-purple-100 focus:text-purple-900">
                        <Link to="/admin/referral" className="flex items-center w-full hover:bg-purple-50 rounded-md transition-colors text-purple-600">
                          <Shield className="mr-2 h-4 w-4" />
                          <span>어드민 - 초대장</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer focus:bg-purple-100 focus:text-purple-900">
                        <Link to="/admin/billing-retry" className="flex items-center w-full hover:bg-purple-50 rounded-md transition-colors text-purple-600">
                          <CreditCard className="mr-2 h-4 w-4" />
                          <span>어드민 - 결제 재시도</span>
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem onClick={() => signOut()} className="text-red-500 hover:text-red-600 focus:text-red-600 cursor-pointer hover:bg-red-50 focus:bg-red-50 rounded-md transition-colors">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>로그아웃</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                size="sm"
                onClick={() => openLoginModal()}
                className="hidden md:inline-flex bg-slate-900 text-white hover:bg-slate-800"
              >
                로그인
              </Button>
            )}

            {/* 햄버거 메뉴 버튼 */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* 모바일 메뉴 */}
        {isMenuOpen && (
          <div className="md:hidden py-2 border-t border-slate-200">
            <div className="flex flex-col gap-1">
              {/* 모바일은 벨 아이콘 대신 메뉴 항목으로 공지사항 제공 */}
              <Link
                to="/notice"
                onClick={() => setIsMenuOpen(false)}
                aria-current={location.pathname === '/notice' ? 'page' : undefined}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${location.pathname === '/notice'
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-600 hover:bg-slate-100/70 hover:text-slate-900'
                  }`}
              >
                <Bell className="h-4 w-4 mr-1.5" />
                공지사항
              </Link>
              {visibleNavItems.map((item) => {
                const isActive = location.pathname === item.path
                const Icon = item.icon
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors
                      ${isActive
                        ? 'bg-slate-100 text-slate-900'
                        : 'text-slate-600 hover:bg-slate-100/70 hover:text-slate-900'
                      }`}
                  >
                    <Icon className="h-4 w-4 mr-1.5" />
                    <span className="flex items-center">
                      {item.label}
                      {item.path === '/deposits' && renderDepositBadge()}
                      {item.isHot && renderHotBadge()}
                    </span>
                  </Link>
                )
              })}
              {/* 모바일 고객센터 버튼 추가 */}
              <button
                onClick={openChannelTalk}
                className="flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors text-slate-600 hover:bg-slate-100/70 hover:text-slate-900"
              >
                <MessageSquare className="h-4 w-4 mr-1.5" />
                고객센터
              </button>
              {user ? (
                <div className="px-3 py-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={user.user_metadata.avatar_url} />
                      <AvatarFallback>{user.user_metadata.nickname?.[0]}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-sm">
                      {user.user_metadata.nickname} 님
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 ml-8">
                    <Link 
                      to="/mypage"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center text-sm py-1.5 px-2 rounded-md text-slate-700 hover:text-slate-900 hover:bg-slate-200/60 transition-colors"
                    >
                      <User className="h-4 w-4 mr-1.5" />
                      마이 페이지
                    </Link>
                    {isAdmin && (
                      <>
                        <Link
                          to="/admin/referral"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center text-sm py-1.5 px-2 rounded-md text-purple-600 hover:text-purple-700 hover:bg-purple-50 transition-colors"
                        >
                          <Shield className="h-4 w-4 mr-1.5" />
                          어드민 - 초대장
                        </Link>
                        <Link
                          to="/admin/billing-retry"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center text-sm py-1.5 px-2 rounded-md text-purple-600 hover:text-purple-700 hover:bg-purple-50 transition-colors"
                        >
                          <CreditCard className="h-4 w-4 mr-1.5" />
                          어드민 - 결제 재시도
                        </Link>
                      </>
                    )}
                    <button 
                      onClick={() => {
                        signOut()
                        setIsMenuOpen(false)
                      }}
                      className="flex items-center text-sm py-1.5 px-2 rounded-md text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-1.5" />
                      로그아웃
                    </button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    openLoginModal()
                    setIsMenuOpen(false)
                  }}
                  className="justify-start px-3 bg-slate-900 text-white hover:bg-slate-800"
                >
                  로그인
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  )
} 